import React, { useState, useEffect } from 'react';
import { Download, Database, Users, Ticket, FileText, Package, CreditCard, TrendingUp, RefreshCw, Calendar, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const DataManagement = () => {
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalOrders: 0,
    totalTickets: 0,
    totalInvoices: 0,
    totalPayments: 0
  });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/data-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDownload = async (dataType) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('customerAdminToken');
      const url = new URL(`${API_BASE_URL}/api/customer-admin/export/${dataType}`);
      
      if (dateRange.startDate) {
        url.searchParams.append('startDate', dateRange.startDate);
      }
      if (dateRange.endDate) {
        url.searchParams.append('endDate', dateRange.endDate);
      }

      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${dataType}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
        toast.success(`${dataType} data downloaded successfully!`);
      } else {
        toast.error('Failed to download data');
      }
    } catch (error) {
      console.error('Error downloading data:', error);
      toast.error('Error downloading data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/export-all-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `complete_database_backup_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Complete database backup downloaded!');
      } else {
        toast.error('Failed to download complete backup');
      }
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Error downloading backup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Database className="h-8 w-8 text-blue-600" />
            Data Management & Reports
          </h2>
          <p className="text-gray-600 mt-1">Download and manage all database records</p>
        </div>
        <Button
          onClick={fetchStats}
          variant="outline"
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh Stats
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-3xl font-bold text-blue-600">{stats.totalCustomers}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200 hover:shadow-lg transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Orders</p>
                <p className="text-3xl font-bold text-green-600">{stats.totalOrders}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Package className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200 hover:shadow-lg transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tickets</p>
                <p className="text-3xl font-bold text-purple-600">{stats.totalTickets}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Ticket className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-orange-200 hover:shadow-lg transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Invoices</p>
                <p className="text-3xl font-bold text-orange-600">{stats.totalInvoices}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <FileText className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-emerald-200 hover:shadow-lg transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Payments</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.totalPayments}</p>
              </div>
              <div className="bg-emerald-100 p-3 rounded-full">
                <CreditCard className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Date Range Filter */}
      <Card className="border-2 border-gray-200">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Date Range Filter (Optional)
          </CardTitle>
          <CardDescription>Select date range for filtered exports</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="mt-2"
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="mt-2"
              />
            </div>
            <Button
              onClick={() => setDateRange({ startDate: '', endDate: new Date().toISOString().split('T')[0] })}
              variant="outline"
            >
              Clear Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Download Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Individual Downloads */}
        <Card className="border-2 border-blue-200 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardTitle className="text-xl">Individual Data Exports</CardTitle>
            <CardDescription>Download specific data sets as CSV files</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-3">
            <Button
              onClick={() => handleDownload('customers')}
              disabled={loading}
              className="w-full justify-between bg-blue-600 hover:bg-blue-700 text-white"
            >
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Download All Customers
              </span>
              <Download className="h-4 w-4" />
            </Button>

            <Button
              onClick={() => handleDownload('orders')}
              disabled={loading}
              className="w-full justify-between bg-green-600 hover:bg-green-700 text-white"
            >
              <span className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Download All Orders
              </span>
              <Download className="h-4 w-4" />
            </Button>

            <Button
              onClick={() => handleDownload('tickets')}
              disabled={loading}
              className="w-full justify-between bg-purple-600 hover:bg-purple-700 text-white"
            >
              <span className="flex items-center gap-2">
                <Ticket className="h-4 w-4" />
                Download All Tickets
              </span>
              <Download className="h-4 w-4" />
            </Button>

            <Button
              onClick={() => handleDownload('invoices')}
              disabled={loading}
              className="w-full justify-between bg-orange-600 hover:bg-orange-700 text-white"
            >
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Download All Invoices
              </span>
              <Download className="h-4 w-4" />
            </Button>

            <Button
              onClick={() => handleDownload('payments')}
              disabled={loading}
              className="w-full justify-between bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <span className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Download Payment History
              </span>
              <Download className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Complete Backup */}
        <Card className="border-2 border-red-200 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-red-50 to-pink-50">
            <CardTitle className="text-xl">Complete Database Backup</CardTitle>
            <CardDescription>Download all data in a single archive file</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center gap-2">
                <Database className="h-5 w-5" />
                Complete Database Export
              </h3>
              <p className="text-sm text-red-800 mb-4">
                This will download a complete backup containing all database records including:
              </p>
              <ul className="space-y-2 text-sm text-red-700 mb-6">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  All customer records
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  Complete order history
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  All support tickets
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  Invoice records
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  Payment transactions
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  System metadata
                </li>
              </ul>
              
              <Button
                onClick={handleDownloadAll}
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white text-lg py-6"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                    Preparing Download...
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5 mr-2" />
                    Download Complete Backup (ZIP)
                  </>
                )}
              </Button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-xs text-yellow-800">
                <strong>⚠️ Note:</strong> Large datasets may take a few moments to prepare. 
                The file will download automatically once ready.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Features */}
      <Card className="border-2 border-indigo-200">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Export Features
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">CSV Format</h4>
              <p className="text-sm text-gray-600">
                All exports are in CSV format, compatible with Excel, Google Sheets, and other tools.
              </p>
            </div>
            <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Date Filtering</h4>
              <p className="text-sm text-gray-600">
                Use date range filter to export data for specific time periods.
              </p>
            </div>
            <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Real-time Data</h4>
              <p className="text-sm text-gray-600">
                All exports contain the most current data from the live database.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataManagement;
